import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTasks, updateTask } from "../../../data/requests/task-requests";
import {
  getHabits,
  checkInHabit,
} from "../../../data/requests/habit-requests";
import { format, isSameDay, startOfWeek, addDays, parseISO } from "date-fns";
import {
  Calendar,
  CheckSquare,
  Target,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

const WEEKDAY_KEYS = [
  "plan.weekdays.mon",
  "plan.weekdays.tue",
  "plan.weekdays.wed",
  "plan.weekdays.thu",
  "plan.weekdays.fri",
  "plan.weekdays.sat",
  "plan.weekdays.sun",
];

export function PlanScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekCursor, setWeekCursor] = useState(new Date());

  // Fetch Tasks
  const { data: tasksResponse } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => getTasks(),
  });
  const tasksData = tasksResponse?.data?.data || tasksResponse?.data || [];
  const tasks = Array.isArray(tasksData) ? tasksData : [];

  // Fetch Habits
  const { data: habitsResponse } = useQuery({
    queryKey: ["habits"],
    queryFn: getHabits,
  });
  const habits = habitsResponse?.data?.data || habitsResponse?.data || [];

  // Helper: Only show habit on or after its creation date
  const isOnOrAfterCreationDate = (habit, date) => {
    if (!habit.createdAt) return true;
    try {
      const createdAt =
        typeof habit.createdAt === "string"
          ? parseISO(habit.createdAt)
          : new Date(habit.createdAt);
      const createdAtDay = new Date(createdAt);
      const targetDay = new Date(date);
      createdAtDay.setHours(0, 0, 0, 0);
      targetDay.setHours(0, 0, 0, 0);
      return targetDay >= createdAtDay;
    } catch {
      return true;
    }
  };

  // Helper: Check if habit has check-in for selected date
  const hasCheckedIn = (habit, date) => {
    if (!habit.logs || habit.logs.length === 0) return false;
    return habit.logs.some((log) => {
      try {
        const logDate =
          typeof log.loggedAt === "string"
            ? parseISO(log.loggedAt)
            : new Date(log.loggedAt);
        return isSameDay(logDate, date);
      } catch {
        return false;
      }
    });
  };

  // Filter habits for selected date
  const todaysHabits = habits
    .filter((habit) => {
      if (!habit.reminderTime) return false;
      if (!isOnOrAfterCreationDate(habit, selectedDate)) return false;
      const dayOfWeek = selectedDate.getDay();
      if (habit.cadence === "weekdays" && (dayOfWeek === 0 || dayOfWeek === 6))
        return false;
      if (habit.cadence === "weekends" && dayOfWeek !== 0 && dayOfWeek !== 6)
        return false;
      return true;
    })
    .map((habit) => ({
      ...habit,
      isCompleted: hasCheckedIn(habit, selectedDate),
    }));

  // Filter tasks for selected date
  const todaysTasks = tasks.filter((task) => {
    if (!task.dueDate) return false;
    try {
      const taskDate =
        typeof task.dueDate === "string"
          ? parseISO(task.dueDate)
          : new Date(task.dueDate);
      return isSameDay(taskDate, selectedDate);
    } catch {
      return false;
    }
  });

  // Separate completed and incomplete tasks
  const incompleteTasks = todaysTasks.filter((t) => !t.isCompleted);
  const completedTasks = todaysTasks.filter((t) => t.isCompleted);

  // Eisenhower Matrix buckets (only incomplete tasks)
  const urgentImportant = incompleteTasks.filter(
    (t) => t.isUrgent && t.isImportant
  );
  const notUrgentImportant = incompleteTasks.filter(
    (t) => !t.isUrgent && t.isImportant
  );
  const urgentNotImportant = incompleteTasks.filter(
    (t) => t.isUrgent && !t.isImportant
  );
  const notUrgentNotImportant = incompleteTasks.filter(
    (t) => !t.isUrgent && !t.isImportant
  );

  const matrixBuckets = [
    {
      key: "urgent-important",
      title: t("plan.doFirst"),
      subtitle: t("plan.urgentImportant"),
      color: "bg-red-500",
      tasks: urgentImportant,
    },
    {
      key: "not-urgent-important",
      title: t("plan.schedule"),
      subtitle: t("plan.notUrgentImportant"),
      color: "bg-blue-500",
      tasks: notUrgentImportant,
    },
    {
      key: "urgent-not-important",
      title: t("plan.delegate"),
      subtitle: t("plan.urgentNotImportant"),
      color: "bg-yellow-500",
      tasks: urgentNotImportant,
    },
    {
      key: "not-urgent-not-important",
      title: t("plan.eliminate"),
      subtitle: t("plan.notUrgentNotImportant"),
      color: "bg-gray-400",
      tasks: notUrgentNotImportant,
    },
  ];

  // ----- Drag & Drop state for Eisenhower matrix -----
  const [bucketOrder, setBucketOrder] = useState({
    "urgent-important": urgentImportant.map((t) => t.id),
    "not-urgent-important": notUrgentImportant.map((t) => t.id),
    "urgent-not-important": urgentNotImportant.map((t) => t.id),
    "not-urgent-not-important": notUrgentNotImportant.map((t) => t.id),
  });

  const [dragItem, setDragItem] = useState(null); // { bucketKey, taskId }

  // Map bucket key -> isUrgent/isImportant flags
  const bucketMeta = {
    "urgent-important": { isUrgent: true, isImportant: true },
    "not-urgent-important": { isUrgent: false, isImportant: true },
    "urgent-not-important": { isUrgent: true, isImportant: false },
    "not-urgent-not-important": { isUrgent: false, isImportant: false },
  };

  // Backend update for bucket change
  const moveMutation = useMutation({
    mutationFn: ({ taskId, bucketKey }) => {
      const meta = bucketMeta[bucketKey];
      return updateTask(taskId, {
        isUrgent: meta.isUrgent,
        isImportant: meta.isImportant,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    // Backend 403 vs diğer hataları UI'ı bozmadan sessizce yut
    onError: () => {
      // No-op: drag-drop sıralamayı lokal state'te bırakalım
    },
  });

  // Keep local order in sync when todaysTasks change
  useEffect(() => {
    setBucketOrder((prev) => {
      const syncBucket = (key, tasks) => {
        const existingOrder = prev[key] || [];
        const taskIds = tasks.map((t) => t.id);

        // 1) Mevcut sıralamadan artık olmayanları at
        const ordered = existingOrder.filter((id) => taskIds.includes(id));
        // 2) Yeni gelen task id'lerini sona ekle
        const newOnes = taskIds.filter((id) => !ordered.includes(id));
        return [...ordered, ...newOnes];
      };

      return {
        "urgent-important": syncBucket("urgent-important", urgentImportant),
        "not-urgent-important": syncBucket(
          "not-urgent-important",
          notUrgentImportant
        ),
        "urgent-not-important": syncBucket(
          "urgent-not-important",
          urgentNotImportant
        ),
        "not-urgent-not-important": syncBucket(
          "not-urgent-not-important",
          notUrgentNotImportant
        ),
      };
    });
  }, [urgentImportant, notUrgentImportant, urgentNotImportant, notUrgentNotImportant]);

  const getOrderedTasksForBucket = (tasks, bucketKey) => {
    const order = bucketOrder[bucketKey] || [];
    const taskMap = new Map(tasks.map((t) => [t.id, t]));

    const ordered = order
      .map((id) => taskMap.get(id))
      .filter(Boolean);

    const remaining = tasks.filter((t) => !order.includes(t.id));

    return [...ordered, ...remaining];
  };

  const handleDragStart = (bucketKey, task) => {
    // Completed Focus Session görevleri backend'de kilitli; drag'i engelle
    if (task?.isCompleted && task?.title?.includes("Focus Session")) return;
    setDragItem({ bucketKey, taskId: task.id });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (bucketKey, targetTaskId) => {
    if (!dragItem) return;

    const fromBucket = dragItem.bucketKey;
    const taskId = dragItem.taskId;

    setBucketOrder((prev) => {
      const next = { ...prev };

      // Remove from source bucket
      if (fromBucket && next[fromBucket]) {
        next[fromBucket] = next[fromBucket].filter((id) => id !== taskId);
      }

      // Ensure destination bucket array exists
      const currentOrder = [...(next[bucketKey] || [])];

      const toIndex = targetTaskId
        ? currentOrder.indexOf(targetTaskId)
        : currentOrder.length;

      if (toIndex === -1) {
        currentOrder.push(taskId);
      } else {
        currentOrder.splice(toIndex, 0, taskId);
      }

      next[bucketKey] = currentOrder;

      return next;
    });

    // Backend'e yeni bucket'a göre isUrgent/isImportant güncelle
    if (bucketMeta[bucketKey]) {
      moveMutation.mutate({ taskId, bucketKey });
    }

    setDragItem(null);
  };

  const handleDropOnEmptyBucket = (bucketKey) => {
    if (!dragItem) return;
    handleDrop(bucketKey, null);
  };

  // Week calendar
  const weekStart = startOfWeek(weekCursor, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const handlePrevWeek = () => setWeekCursor(addDays(weekCursor, -7));
  const handleNextWeek = () => setWeekCursor(addDays(weekCursor, 7));
  const handleToday = () => {
    const today = new Date();
    setWeekCursor(today);
    setSelectedDate(today);
  };

  // Habit check-in from plan screen
  const habitCheckInMutation = useMutation({
    mutationFn: (habitId) => checkInHabit(habitId, { note: "" }),
    onSuccess: async (_data, habitId) => {
      // Invalidate and refetch immediately
      await queryClient.invalidateQueries({ queryKey: ["habits"] });
      await queryClient.invalidateQueries({ queryKey: ["habit", habitId] });
      await queryClient.refetchQueries({ queryKey: ["habits"] });
      await queryClient.refetchQueries({ queryKey: ["habit", habitId] });
      toast({
        title: t("habits.checkedIn"),
        description: t("habits.keepStreak"),
      });
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message || t("habits.checkInFailed");
      toast({
        title: t("common.error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleHabitCheckIn = (habitId) => {
    if (!habitId) return;
    habitCheckInMutation.mutate(habitId);
  };

  return (
    <AppLayout title={t("plan.title")} description={t("plan.subtitle")}>
      <div className="space-y-6">
        {/* Week Calendar */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t("plan.weekView")}
                </CardTitle>
                <CardDescription className="mt-1">
                  {format(weekStart, "dd.MM")} -{" "}
                  {format(addDays(weekStart, 6), "dd.MM.yyyy")}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handlePrevWeek}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleToday}>
                  {t("plan.today")}
                </Button>
                <Button variant="outline" size="sm" onClick={handleNextWeek}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day, index) => {
                const isSelected = isSameDay(day, selectedDate);
                const isToday = isSameDay(day, new Date());
                const dayTasks = tasks.filter((t) => {
                  if (!t.dueDate) return false;
                  try {
                    const taskDate =
                      typeof t.dueDate === "string"
                        ? parseISO(t.dueDate)
                        : new Date(t.dueDate);
                    return isSameDay(taskDate, day) && !t.isCompleted;
                  } catch {
                    return false;
                  }
                });
                const dayHabits = habits.filter(
                  (h) => h.reminderTime && isOnOrAfterCreationDate(h, day)
                );

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(day)}
                    className={cn(
                      "p-4 rounded-xl border-2 transition-all text-left hover:border-primary/50",
                      isSelected && "border-primary bg-primary/5",
                      !isSelected && "border-border"
                    )}
                  >
                    <div className="space-y-2">
                      <div>
                        <div
                          className={cn(
                            "text-xs font-medium uppercase",
                            isToday && "text-primary"
                          )}
                        >
                          {t(WEEKDAY_KEYS[index])}
                        </div>
                        <div
                          className={cn(
                            "text-2xl font-bold",
                            isToday && "text-primary"
                          )}
                        >
                          {format(day, "d")}
                        </div>
                      </div>
                      <div className="space-y-1">
                        {dayTasks.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <CheckSquare className="h-3 w-3" />
                            {dayTasks.length}
                          </div>
                        )}
                        {dayHabits.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Target className="h-3 w-3" />
                            {dayHabits.length}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Info */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">
              {isSameDay(selectedDate, new Date())
                ? t("plan.today")
                : format(selectedDate, "dd.MM.yyyy")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {incompleteTasks.length} {t("plan.tasks")} · {todaysHabits.length}{" "}
              {t("plan.habitsScheduled")}
            </p>
          </div>
        </div>

        {/* Habits for the day */}
        {todaysHabits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                {t("plan.todaysHabits")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {todaysHabits.map((habit) => (
                  <button
                    key={habit.id}
                    type="button"
                    onClick={() => navigate(`/habits/${habit.id}`)}
                    className={cn(
                      "w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all text-left",
                      habit.isCompleted
                        ? "border-green-500/50 bg-green-500/5"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                          habit.isCompleted
                            ? "border-green-500 bg-green-500"
                            : "border-muted-foreground bg-background hover:border-primary"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!habit.isCompleted) {
                            handleHabitCheckIn(habit.id);
                          }
                        }}
                        aria-label={t("habits.card.checkIn")}
                        disabled={habit.isCompleted}
                      >
                        {habit.isCompleted && (
                          <CheckSquare className="h-3 w-3 text-white" />
                        )}
                      </button>
                      <div>
                        <div
                          className={cn(
                            "font-medium",
                            habit.isCompleted &&
                              "line-through text-muted-foreground"
                          )}
                        >
                          {habit.name}
                        </div>
                        {habit.reminderTime && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Clock className="h-3 w-3" />
                            {habit.reminderTime}
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge variant="secondary">
                      {habit.targetPerWeek}x/week
                    </Badge>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Eisenhower Matrix */}
        <div>
          <h3 className="text-lg font-semibold mb-4">
            {t("plan.eisenhowerMatrix")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {matrixBuckets.map((bucket) => (
              <Card key={bucket.key} className="border-2">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={cn("w-3 h-3 rounded-full", bucket.color)} />
                    <div>
                      <CardTitle className="text-base">
                        {bucket.title}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {bucket.subtitle}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {bucket.tasks.length > 0 ? (
                    <div className="space-y-2">
                      {getOrderedTasksForBucket(
                        bucket.tasks,
                        bucket.key
                      ).map((task) => {
                        const isLocked =
                          task.isCompleted &&
                          typeof task.title === "string" &&
                          task.title.includes("Focus Session");
                        return (
                          <div
                            key={task.id}
                            className={cn(
                              "p-3 rounded-lg border bg-card transition-colors",
                              isLocked
                                ? "cursor-not-allowed opacity-75"
                                : "hover:bg-accent/50 cursor-move"
                            )}
                            draggable={!isLocked}
                            onDragStart={() =>
                              !isLocked && handleDragStart(bucket.key, task)
                            }
                            onDragOver={handleDragOver}
                            onDrop={() => handleDrop(bucket.key, task.id)}
                            onClick={() => navigate(`/tasks/${task.id}`)}
                          >
                          <div className="font-medium text-sm">
                            {task.title}
                          </div>
                            {task.description && (
                              <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                {task.description}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className="text-center py-8 text-sm text-muted-foreground"
                      onDragOver={handleDragOver}
                      onDrop={() => handleDropOnEmptyBucket(bucket.key)}
                    >
                      {t("plan.noTasksInQuadrant")}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Completed Tasks Section */}
        {completedTasks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-muted-foreground">
                {t("home.completed")} ({completedTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {completedTasks.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border-2 border-muted-foreground/30 bg-muted/30 hover:bg-muted/50 transition-all text-left opacity-75"
                  >
                    <div className="flex items-center gap-3">
                      <CheckSquare className="h-4 w-4 text-green-500" />
                      <div>
                        <div className="font-medium text-sm line-through text-muted-foreground">
                          {task.title}
                        </div>
                        {task.description && (
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {task.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state if no tasks or habits */}
        {incompleteTasks.length === 0 && todaysHabits.length === 0 && completedTasks.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t("plan.nothingScheduled")}
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                {t("plan.nothingScheduledDesc", {
                  date: format(selectedDate, "dd.MM.yyyy"),
                })}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}

export default PlanScreen;
